import type React from 'react';

import { Box } from '@zillow/constellation-10';

const ICON_SIZE_MAP = {
  sm: '200',
  md: '400',
  lg: '600',
} as const;

const PADDING_LEFT_MAP = {
  sm: '100',
  md: '200',
  lg: '400',
} as const;

export type IconifiedListItem = {
  icon?: React.ReactNode;
  primaryContent: React.ReactNode;
  secondaryContent?: React.ReactNode;
};

type IconifiedListProps = {
  listItems: Array<IconifiedListItem>;
  iconSize?: 'sm' | 'md' | 'lg';
  backgroundColor?: string;
  verticalLineColor?: string;
  className?: string;
};

const VERTICAL_LINE_COLOR_MAP: Record<string, string> = {
  borderLight: 'border.subtle',
  brand: 'accent.brand.hero',
};

const IconifiedList = ({
  listItems,
  iconSize = 'md',
  backgroundColor = 'white',
  verticalLineColor = 'border.subtle',
  className,
}: IconifiedListProps) => {
  const sizeToken = ICON_SIZE_MAP[iconSize];
  const paddingLeft = PADDING_LEFT_MAP[iconSize];
  const lineColor = VERTICAL_LINE_COLOR_MAP[verticalLineColor] ?? verticalLineColor;

  return (
    <Box
      className={className}
      css={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor,
      }}
    >
      {listItems.map((item, i) => {
        const { icon, primaryContent, secondaryContent } = item;
        const isLast = i === listItems.length - 1;
        return (
          <Box
            key={`list-item-${i}`}
            css={{
              display: 'flex',
              position: 'relative',
              paddingBottom: isLast ? 0 : sizeToken,
              backgroundColor,
            }}
          >
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: sizeToken,
                zIndex: 1,
                backgroundColor,
              }}
            >
              {!isLast && (
                <Box
                  className="vertical-line"
                  css={{
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    backgroundColor: lineColor,
                    transform: 'translateX(-50%)',
                  }}
                />
              )}
            </Box>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: sizeToken,
                height: sizeToken,
                minWidth: sizeToken,
                flexShrink: 0,
                lineHeight: 0,
                zIndex: 2,
                backgroundColor,
                '& svg': { width: '100%', height: '100%', flexShrink: 0 },
              }}
            >
              {icon}
            </Box>
            <Box
              className="IconifiedList-ItemContent"
              css={{
                paddingLeft,
                minWidth: 0,
                flex: 1,
                backgroundColor,
              }}
            >
              <Box
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: sizeToken,
                  minWidth: 0,
                }}
              >
                {primaryContent}
              </Box>
              {secondaryContent != null && secondaryContent !== '' && <Box>{secondaryContent}</Box>}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default IconifiedList;
